import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Wallet, TrendingUp, Lock, ArrowUpRight, Download, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate, BACKEND } from "@/lib/api";
import { PageHeader, Pager, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function WalletPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");

  const { data: wallet } = useQuery({ queryKey: ["wallet"], queryFn: async () => (await api.get("/agent/wallet")).data });
  const { data: ledger } = useQuery({
    queryKey: ["agent-ledger", page],
    queryFn: async () => (await api.get(`/accounting/ledger?page=${page}&limit=15`)).data,
  });

  const withdraw = useMutation({
    mutationFn: async () => (await api.post("/agent/wallet/withdraw", { amount: Number(amount) })).data,
    onSuccess: () => {
      toast.success("Withdrawal requested — payout goes out in the daily batch after admin approval");
      setOpen(false);
      setAmount("");
      qc.invalidateQueries({ queryKey: ["wallet"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const cards = [
    { testid: "wallet-available", icon: Wallet, label: "Available to withdraw", value: fmtINR(wallet?.available ?? 0), color: "#FF6B00" },
    { testid: "wallet-balance", icon: TrendingUp, label: "Lifetime earnings balance", value: fmtINR(wallet?.balance ?? 0), color: "#138808" },
    { testid: "wallet-held", icon: Lock, label: "On hold (pending withdrawals)", value: fmtINR(wallet?.held ?? 0), color: "#1D5BD8" },
  ];

  return (
    <div data-testid="wallet-page">
      <PageHeader title="Wallet" sub="Profit from delivered orders lands here after cost, marketplace fees and taxes. Withdraw anytime above the minimum." testid="wallet-title">
        <Button data-testid="export-ledger-btn" size="sm" variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-50"
          onClick={() => window.open(`${BACKEND}/api/export/ledger`, "_blank")}>
          <Download size={13} /> Statement CSV
        </Button>
        <Button data-testid="withdraw-btn" onClick={() => setOpen(true)} disabled={!wallet?.can_withdraw}
          className="bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">
          <ArrowUpRight size={14} /> Withdraw
        </Button>
      </PageHeader>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
              <c.icon size={15} style={{ color: c.color }} />
            </div>
            <p className="mt-1.5 text-2xl font-extrabold text-[#0F2440]">{c.value}</p>
          </div>
        ))}
      </div>

      {wallet && !wallet.kyc_complete && (
        <div data-testid="wallet-kyc-warning" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
          <span className="flex items-center gap-2"><ShieldAlert size={14} /> Complete your KYC (bank details, PAN, GSTIN) to enable withdrawals.</span>
          <Link to="/agent/kyc" data-testid="wallet-goto-kyc" className="font-bold text-[#E85D04] hover:underline">Complete KYC →</Link>
        </div>
      )}
      {wallet?.kyc_complete && !wallet.can_withdraw && (wallet.available ?? 0) < (wallet.min_withdrawal ?? 500) && (
        <p data-testid="wallet-min-hint" className="mb-5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
          Withdrawals unlock at <b className="text-[#0F2440]">{fmtINR(wallet.min_withdrawal)}</b> available balance. Keep selling — profit credits when orders are delivered.
        </p>
      )}

      <h3 className="mb-3 text-sm font-bold text-[#0F2440]">Withdrawals</h3>
      {!wallet?.withdrawals?.length ? <p className="mb-6 text-xs text-slate-400" data-testid="no-withdrawals">No withdrawals yet.</p> : (
        <div className="mb-6 space-y-2" data-testid="withdrawals-list">
          {wallet.withdrawals.map((w) => (
            <div key={w.id} data-testid={`withdrawal-${w.id}`} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-4 py-3 text-xs shadow-sm">
              <div>
                <p className="font-semibold text-[#0F2440]">{fmtINR(w.amount)}</p>
                <p className="text-[10px] text-slate-500">{fmtDate(w.created_at)}{w.payout_id ? ` · ${w.payout_id}` : ""}{w.reason ? ` · ${w.reason}` : ""}</p>
              </div>
              <StatusBadge status={w.status} />
            </div>
          ))}
        </div>
      )}

      <h3 className="mb-3 text-sm font-bold text-[#0F2440]">Earnings statement</h3>
      {!ledger?.items?.length ? <EmptyState title="No entries yet" sub="Profit credits when your orders are delivered." /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white shadow-sm">
          <table className="w-full text-left text-xs" data-testid="ledger-table">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {ledger.items.map((e) => (
                <tr key={e.id} data-testid={`ledger-row-${e.id}`} className="border-b border-slate-50">
                  <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[10px] text-slate-500">{fmtDate(e.created_at)}</td>
                  <td className="px-4 py-2.5 text-slate-600">{e.note}</td>
                  <td className="px-4 py-2.5"><span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] capitalize text-slate-500">{e.category.replace(/_/g, " ")}</span></td>
                  <td className="px-4 py-2.5 font-mono text-[10px] text-slate-500">{e.ref}</td>
                  <td className={`px-4 py-2.5 text-right font-mono font-semibold ${e.type === "credit" ? "text-[#138808]" : "text-rose-600"}`}>
                    {e.type === "credit" ? "+" : "−"}{fmtINR(e.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {ledger && <Pager page={ledger.page} pages={ledger.pages} onPage={setPage} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-200 bg-white text-[#0F2440]">
          <DialogHeader><DialogTitle>Request withdrawal</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            Available: <b className="text-[#0F2440]">{fmtINR(wallet?.available ?? 0)}</b> · Minimum {fmtINR(wallet?.min_withdrawal ?? 500)} ·
            Paid to your KYC bank account via Razorpay in the daily payout batch.
          </p>
          <Input data-testid="withdraw-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder={`Amount (min ${wallet?.min_withdrawal ?? 500})`} className="border-slate-200 bg-white" />
          <Button data-testid="withdraw-submit-btn" onClick={() => withdraw.mutate()} disabled={withdraw.isPending || !amount}
            className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">
            {withdraw.isPending ? "Requesting…" : "Request withdrawal"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
